import {
  GlobeViewMapType,
  IGlobeViewStyle,
} from '~/client/graph/types.generated'

import MapViewItemType from '../../enums/MapViewItemType'

export enum MapboxSourceType {
  GeoJSON = 'geojson',
  Vector = 'vector',
  Raster = 'raster',
  Image = 'image',
  Video = 'video',
}

export const DEFAULT_GLOBE_VIEW_STYLE: IGlobeViewStyle = {
  mapType: GlobeViewMapType.Street,
  isThreeDEnabled: false,
  isTrafficEnabled: false,
  isTerrainEnabled: false,
  shouldHideLabels: false,
  isZoomScalingEnabled: true,
  isLabelOverlapEnabled: false,
  shouldHideWorkflowPinLabels: false,
}

// One resolver for both viewers. Overlap defaults ON: saved views predate that toggle.
export function resolveGlobeViewStyle(
  style?: IGlobeViewStyle | null,
): IGlobeViewStyle {
  return {
    mapType: style?.mapType || DEFAULT_GLOBE_VIEW_STYLE.mapType,
    isThreeDEnabled: !!style?.isThreeDEnabled,
    isTrafficEnabled: !!style?.isTrafficEnabled,
    isTerrainEnabled: !!style?.isTerrainEnabled,
    shouldHideLabels: !!style?.shouldHideLabels,
    // Not DEFAULT_GLOBE_VIEW_STYLE: that is the shape for no view at all. A saved view predating
    // either toggle reads scaling off, overlap on — aligning them resizes markers and drops labels.
    isZoomScalingEnabled: !!style?.isZoomScalingEnabled,
    isLabelOverlapEnabled: style?.isLabelOverlapEnabled ?? true,
    shouldHideWorkflowPinLabels: !!style?.shouldHideWorkflowPinLabels,
  }
}

// A line or a text sticker is a drawing primitive, so it may exist with no location behind it.
export const ALLOWED_SITEMAP_ITEMS_WITHOUT_DATA_OBJECT = [
  MapViewItemType.Line,
  MapViewItemType.TextBox,
]

export const SHAPE_FILL_LAYER = 'shape_fill'
export const SHAPE_LINE_LAYER = 'shape_line'
export const SHAPE_ARROW_LINE_LAYER = 'shape_arrow_line'

export const WORKFLOW_PINS_LAYER = 'workflow-pins'
export const DELIVERY_PINS_LAYER = 'delivery-pins'
export const SHARED_PINS_LAYER = 'shared-pins'

// A pin set's selected twins, single then counted.
export const pinTwinLayerIds = (layerId: string): string[] => [
  `${layerId}-exempt`,
  `${layerId}-exempt-counted`,
]

// Both pin label layers are captions, held below every location layer (mapboxLayerOrder).
// Declared here because both the layers and the code that orders them need the same names.
export const WORKFLOW_PIN_LABELS_LAYER = 'workflow-pin-labels'
// Anchored labels place dynamically around a location's own name, which requires them to be
// considered AFTER it — so this layer is the lowest caption of all.
export const WORKFLOW_PIN_LABELS_ANCHORED_LAYER = 'workflow-pin-labels-anchored'

// The selected tier (STX1-1088): a selected item paints from these twins, which sit above every
// pin, so it and its label outrank unselected siblings. A label's or location's resting copy
// stays in its layer, hidden by paint; a selected pin is filtered out of its resting layers.
export const WORKFLOW_PIN_LABELS_SELECTED_LAYER = 'workflow-pin-labels-selected'
export const WORKFLOW_PIN_LABELS_SELECTED_ANCHORED_LAYER =
  'workflow-pin-labels-selected-anchored'
export const SHARED_PINS_CHIP_LAYER = 'shared-pins-chip'
export const SHARED_PINS_CHIP_SELECTED_LAYER = 'shared-pins-chip-selected'

// A map view item has no id until it is saved — the factory builds its data object with a null
// one — so an item being PLACED has nothing for the edit path to key on. Isolation, the
// selection ring and the grab hit-test all need the same answer for it or they disagree about
// which item is being edited, so they agree on this instead.
//
// It never leaves the render path: it is not written to the item and never reaches the backend.
// Before the selection was drawn from the live layers, the editor's own DOM copy both drew and
// dragged the draft, which is why a null id did not matter until now.
export const DRAFT_MAP_VIEW_ITEM_ID = 'map-view-item-draft'
export const mapViewItemKey = (id: string | null | undefined): string =>
  id ?? DRAFT_MAP_VIEW_ITEM_ID

// The markup draw controller's own layers (MarkupDrawController), declared here for the layer
// order: it raises them while a markup is edited and lowers them with the markup geometry after.
export const MARKUP_EDIT_LAYER = {
  linePreview: 'markup-line-preview',
  fillPreview: 'markup-fill-preview',
  fillPreviewOutline: 'markup-fill-preview-outline',
  arrowheads: 'markup-edit-arrowheads',
  arrowdots: 'markup-edit-arrowdots',
  strokesSolid: 'markup-edit-strokes-solid',
  strokesDashed: 'markup-edit-strokes-dashed',
  handles: 'markup-edit-handles',
  drawVertices: 'markup-draw-vertices',
  activeCasing: 'markup-active-casing',
  activeStrokeSolid: 'markup-active-stroke-solid',
  activeStrokeDashed: 'markup-active-stroke-dashed',
  activeArrowheads: 'markup-active-arrowheads',
  activeArrowdots: 'markup-active-arrowdots',
  activeHandles: 'markup-active-handles',
  hover: 'markup-edit-hover',
} as const
export const MARKUP_EDIT_LAYER_IDS: string[] = Object.values(MARKUP_EDIT_LAYER)

export const LOCATION_MARKERS_LAYER = 'location-markers'
// The selected tier's copy of a location whose card is open (STX1-1088).
export const LOCATION_MARKERS_SELECTED_LAYER = 'location-markers-selected'

// An item lands on exactly one of these, so anything hit-testing has to look at
// all of them. Bottom to top, as they mount: the names sit under every circle, so the circles
// place first.
export const locationMarkerLayerIds = (layerId: string): string[] => [
  `${layerId}-label`,
  `${layerId}-label-boxed`,
  `${layerId}-label-boxed-center`,
  layerId,
  `${layerId}-icon`,
  `${layerId}-icon-boxed`,
]
export const LOCATION_MARKERS_SOURCE = 'location-markers-source'

// One id set serves every viewer: each mounts MapLocationLayers into its own mapbox style.
export const SHAPE_FILL_SOURCE = 'fill_shape_data'
export const SHAPE_LINE_SOURCE = 'line_shape_data'
export const SHAPE_ARROW_LINE_SOURCE = 'arrows_shape_data'

export const PLAN_SOURCE = 'plan_source'
export const TILESET_SOURCE = 'tileset_source'
export const REMOTE_TILESET_LAYER = 'remote-tileset_layer'

export const UNDER_PLAN_FILL_LAYER = 'under-plan-fill'
export const UNDER_PLAN_LINE_LAYER = 'under-plan-line'

export const ITEM_EDITOR_SHAPE_FILL_LAYER = 'item_editor_shape_fill'
export const ITEM_EDITOR_SHAPE_LINE_LAYER = 'item_editor_shape_line'
export const ITEM_EDITOR_ARROW_LINE_LAYER = 'item_editor_arrows_line'
export const SHAPE_EDITABLE_POINTS_LAYER = 'shape_editable_points'
export const SHAPE_SELECTED_POINTS_LAYER = 'shape_selected_points'
export const POLYLINE_ADDITIONAL_EDITABLE_POINTS_LAYER =
  'polyline_additional_editable_points'

export const PLAN_LAYER_PREFIX = 'plan_layer_'
export const REMOTE_TILESET_LAYER_PREFIX = 'remote-tileset_layer_'
export const RASTER_TILESET_FILL_LAYER_PREFIX = 'raster_tile_fill_'
export const TILESET_FILL_LAYER_PREFIX = 'tile_fill_'
export const TILESET_FILL_OUTLINE_LAYER_PREFIX = 'tile_fill_outline_'
export const TILESET_LINE_LAYER_PREFIX = 'tile_line_'
export const TILESET_CIRCLE_LAYER_PREFIX = 'tile_circle_'
export const TILESET_SYMBOL_LAYER_PREFIX = 'tile_symbol_'

export const TILESET_PREFIXES = [
  TILESET_FILL_LAYER_PREFIX,
  TILESET_LINE_LAYER_PREFIX,
  TILESET_CIRCLE_LAYER_PREFIX,
  TILESET_SYMBOL_LAYER_PREFIX,
]

// Map File overlay layers that must render beneath the drawn Location shapes.
export const MAP_FILE_OVERLAY_LAYER_PREFIXES = [
  PLAN_LAYER_PREFIX,
  REMOTE_TILESET_LAYER_PREFIX,
  RASTER_TILESET_FILL_LAYER_PREFIX,
  ...TILESET_PREFIXES,
]

// Mapbox's default clickTolerance: within it a press is still reported as a click, not a drag.
export const MAPBOX_CLICK_TOLERANCE_PX = 3
